import { api } from './api';

type OauthClient = {
  client_id: string;
  client_name: string;
  logo_uri?: string;
  client_uri?: string;
};

type ValidateOk = {
  ok: true;
  client: OauthClient;
  requested_scopes: string[];
  // The ceiling of scopes this client may be granted: ['*'] for a public
  // client, or the owning service account's allowed_scopes for a confidential
  // one. The consent screen must not let the user grant beyond this.
  grantable_scopes: string[];
  state?: string;
};

type ValidateError = {
  ok: false;
  fatal: boolean;
  error: string;
  error_description?: string;
};

type ValidateResult = ValidateOk | ValidateError;

type AuthorizeParams = {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope?: string;
  state?: string;
  code_challenge: string;
  code_challenge_method: string;
  resource?: string;
};

type RedirectResult = { redirect_uri: string };

type ConnectedApplication = {
  client: OauthClient;
  scopes: string[];
  last_use: string | null;
  authorized_at: string | null;
};

type ConnectedApplicationsResult = {
  connected_applications: ConnectedApplication[];
};

const oauthApi = api.injectEndpoints({
  endpoints: (builder) => ({
    validateAuthorize: builder.query<ValidateResult, AuthorizeParams>({
      query: (params) => ({
        url: 'oauth/authorize/validate',
        params,
      }),
    }),
    approveAuthorize: builder.mutation<
      RedirectResult,
      AuthorizeParams & { scopes: string[] }
    >({
      query: (body) => ({
        url: 'oauth/authorize/approve',
        method: 'POST',
        body,
      }),
    }),
    denyAuthorize: builder.mutation<RedirectResult, AuthorizeParams>({
      query: (body) => ({
        url: 'oauth/authorize/deny',
        method: 'POST',
        body,
      }),
    }),
    connectedApplications: builder.query<ConnectedApplicationsResult, void>({
      query: () => 'oauth/connected-applications',
    }),
    revokeApplication: builder.mutation<void, string>({
      query: (clientId) => ({
        url: 'oauth/connected-applications',
        method: 'DELETE',
        body: { client_id: clientId },
      }),
      onQueryStarted: async (clientId, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          oauthApi.util.updateQueryData(
            'connectedApplications',
            undefined,
            (current) => {
              current.connected_applications =
                current.connected_applications.filter(
                  (a) => a.client.client_id !== clientId,
                );
            },
          ),
        );
        queryFulfilled.catch(patch.undo);
      },
    }),
  }),
});

export const {
  useValidateAuthorizeQuery,
  useApproveAuthorizeMutation,
  useDenyAuthorizeMutation,
  useConnectedApplicationsQuery,
  useRevokeApplicationMutation,
} = oauthApi;

export type {
  OauthClient,
  ValidateResult,
  ValidateOk,
  ValidateError,
  AuthorizeParams,
  ConnectedApplication,
};

export { oauthApi };
